<template>
  <div v-if="show" class="rule-info-overlay" @click.self="close">
    <div class="rule-info-modal">
      <!-- Header -->
      <div class="rim-header" :class="actionBgClass">
        <div class="rim-header-content">
          <div class="rim-icon-wrap">
            <i class="material-icons">{{ info.icon }}</i>
          </div>
          <div>
            <h5 class="rim-title">{{ info.title }}</h5>
            <span class="rim-action-badge" :class="actionBadgeClass">
              <i class="material-icons" style="font-size:14px;">{{ actionIcon }}</i>
              {{ actionLabel }}
            </span>
          </div>
        </div>
        <button class="rim-close" @click="close">
          <i class="material-icons">close</i>
        </button>
      </div>

      <!-- Body -->
      <div class="rim-body">
        <!-- Action Type Explainer -->
        <div class="rim-action-box" :class="actionBoxClass">
          <div class="rim-action-icon-lg">
            <i class="material-icons">{{ actionIcon }}</i>
          </div>
          <div>
            <strong>{{ actionLabel }} Rule</strong>
            <p class="mb-0">{{ actionExplanation }}</p>
          </div>
        </div>

        <!-- What it does -->
        <div class="rim-section">
          <h6 class="rim-section-title"><i class="material-icons">description</i> What This Rule Does</h6>
          <p class="rim-text">{{ info.explanation }}</p>
        </div>

        <!-- How it works — visual steps -->
        <div class="rim-section">
          <h6 class="rim-section-title"><i class="material-icons">account_tree</i> How It Works</h6>
          <div class="rim-steps">
            <div v-for="(step, idx) in info.steps" :key="idx" class="rim-step">
              <div class="rim-step-number" :class="actionBgClass">{{ idx + 1 }}</div>
              <div class="rim-step-icon-wrap">
                <i class="material-icons">{{ step.icon }}</i>
              </div>
              <div class="rim-step-text">{{ step.text }}</div>
              <div v-if="idx < info.steps.length - 1" class="rim-step-arrow">
                <i class="material-icons">arrow_downward</i>
              </div>
            </div>
          </div>
        </div>

        <!-- Outcome -->
        <div class="rim-section">
          <h6 class="rim-section-title"><i class="material-icons">emoji_objects</i> Outcome</h6>
          <div class="rim-outcome" :class="actionBoxClass">
            <i class="material-icons rim-outcome-icon">{{ info.actionType === 'never' ? 'block' : 'tune' }}</i>
            <span>{{ info.outcome }}</span>
          </div>
        </div>

        <!-- Example -->
        <div class="rim-section">
          <h6 class="rim-section-title"><i class="material-icons">lightbulb</i> Example</h6>
          <div class="rim-example">
            <i class="material-icons rim-example-icon">format_quote</i>
            <p class="mb-0">{{ info.example }}</p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="rim-footer">
        <button class="rim-footer-btn" @click="close">Got it</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  ruleId: { type: [Number, String], default: null },
  /** 'user' or 'workgroup' — selects which rule data map to use */
  ruleContext: { type: String, default: 'user' },
  /** Optional override info object (for workgroup rules that come from API) */
  customInfo: { type: Object, default: null },
});

const emit = defineEmits(['update:show', 'close']);
function close() {
  emit('update:show', false);
  emit('close');
}

/* ── User rule info data ── */
const USER_RULES = {
  1: {
    title: 'Schedule Near Another Person',
    icon: 'group',
    actionType: 'both',
    explanation:
      'This rule controls whether this person should be scheduled close in time to specific other people. You pick one or more people and a number of days. The scheduler will then try to schedule (or avoid scheduling) this person within that many days of the selected people.',
    steps: [
      { icon: 'people', text: 'Select person(s) you want to link scheduling with' },
      { icon: 'date_range', text: 'Set the number of days as the window' },
      { icon: 'rule', text: 'Choose "Never" to prevent or "When Possible" to prefer' },
      { icon: 'event_available', text: 'Scheduler evaluates the time gap between assignments' },
    ],
    outcome: 'If "Never": this person will NOT be scheduled within the specified days of the selected people. If "When Possible": the scheduler will TRY to schedule them close together, but may override this if needed.',
    example: '"Never schedule me within 7 days of John Smith" — if John is scheduled on Sunday the 5th, this person will be excluded from any slots between the 5th and the 12th.',
  },
  2: {
    title: 'Do Not Schedule With Certain People',
    icon: 'person_off',
    actionType: 'both',
    explanation:
      'This rule prevents (or discourages) this person from being scheduled at the same time as specific other people. Use this when two volunteers should not serve together on the same date or event.',
    steps: [
      { icon: 'people', text: 'Select person(s) who should not be scheduled together with this user' },
      { icon: 'rule', text: 'Choose "Never" to strictly block or "When Possible" to discourage' },
      { icon: 'event', text: 'Scheduler checks each assignment for overlapping people' },
    ],
    outcome: 'If "Never": both people will never appear on the same assignment. If "When Possible": the scheduler will try to keep them apart but may place them together if no other option exists.',
    example: '"Never schedule me with Jane Doe" — if Jane is assigned to the 9 AM Greeting team on March 10, this person will be excluded from that same slot.',
  },
  3: {
    title: 'Only Schedule With Certain People',
    icon: 'people_alt',
    actionType: 'both',
    explanation:
      'This rule ensures (or prefers) that this person is only scheduled alongside specific other people. This is useful for couples, mentors, or partners who want to serve together.',
    steps: [
      { icon: 'people', text: 'Select person(s) this user should be paired with' },
      { icon: 'rule', text: 'Choose "Never" to strictly require or "When Possible" to prefer' },
      { icon: 'event', text: 'Scheduler checks if at least one selected person is also assigned' },
    ],
    outcome: 'If "Never": this person will only be scheduled when at least one of the selected people is also scheduled. If "When Possible": the scheduler will prefer to pair them but may schedule this person alone if needed.',
    example: '"Only schedule me with Bob Johnson" — this person will only appear on assignments where Bob is also assigned.',
  },
  4: {
    title: 'Preferred Workgroup',
    icon: 'workspaces',
    actionType: 'when-possible',
    explanation:
      'This rule tells the scheduler that the person prefers to be assigned to a specific workgroup. The scheduler will try to honor this preference when building the schedule, but it is not a hard requirement.',
    steps: [
      { icon: 'group_work', text: 'Select the preferred workgroup from the dropdown' },
      { icon: 'auto_awesome', text: 'Scheduler gives higher priority to this workgroup for the person' },
      { icon: 'event_available', text: 'Person is more likely to be placed in the preferred workgroup' },
    ],
    outcome: 'The scheduler will prioritize placing this person in the selected workgroup. However, if slots are full or constraints conflict, they may be assigned elsewhere.',
    example: '"When possible, schedule me in the Audio/Visual workgroup" — this person will be assigned to A/V first, but could be placed in Ushers if A/V is full.',
  },
  5: {
    title: 'Maximum Frequency (Hard Limit)',
    icon: 'event_busy',
    actionType: 'never',
    explanation:
      'This rule sets an absolute limit on how many times the person can be scheduled within a given number of days. If the limit is reached, the person is completely excluded from further scheduling within that window.',
    steps: [
      { icon: 'pin', text: 'Set the maximum number of times (e.g. 2)' },
      { icon: 'date_range', text: 'Set the number of days for the window (e.g. 14)' },
      { icon: 'calculate', text: 'Scheduler counts existing assignments within the window' },
      { icon: 'block', text: 'Person is excluded once the count reaches the limit' },
    ],
    outcome: 'The person will be completely blocked from scheduling once they reach the specified number of assignments within the time window. This is a hard limit.',
    example: '"Never schedule me more than 2 times within 14 days" — if the person is already scheduled twice in a 14-day span, they will not appear as a candidate for any additional slots in that window.',
  },
  6: {
    title: 'Workgroup Frequency Preference',
    icon: 'tune',
    actionType: 'when-possible',
    explanation:
      'This rule sets a preferred frequency limit for a specific workgroup. The scheduler will try to avoid exceeding this limit, but it is a soft preference — the person will NOT be excluded if the limit is exceeded.',
    steps: [
      { icon: 'pin', text: 'Set the preferred maximum times (e.g. 1)' },
      { icon: 'date_range', text: 'Set the number of days for the window (e.g. 7)' },
      { icon: 'group_work', text: 'Select the workgroup this limit applies to' },
      { icon: 'tune', text: 'Scheduler prefers to stay within the limit but may exceed it' },
    ],
    outcome: 'The scheduler will try to keep this person at or below the specified frequency for the selected workgroup, but will still consider them if no better options exist.',
    example: '"When possible, do not schedule me more than 1 time within 7 days for the Greeter workgroup" — the scheduler will prefer other greeters after this person has served once that week, but could still assign them if needed.',
  },
  7: {
    title: 'Separate Two Workgroups',
    icon: 'swap_horiz',
    actionType: 'when-possible',
    explanation:
      'This rule tells the scheduler to try to avoid scheduling this person in two specific workgroups that are close together in time. This helps distribute effort across different roles.',
    steps: [
      { icon: 'group_work', text: 'Select the first workgroup' },
      { icon: 'group_work', text: 'Select the second workgroup' },
      { icon: 'date_range', text: 'Set the minimum day gap between them' },
      { icon: 'tune', text: 'Scheduler tries to maintain the gap between assignments' },
    ],
    outcome: 'The scheduler will prefer not to assign this person to both workgroups within the specified time span, but may still do so if scheduling constraints require it.',
    example: '"When possible, separate my assignments in Choir and Band by at least 7 days" — if assigned to Choir on March 3, the scheduler will try to avoid scheduling Band before March 10.',
  },
  8: {
    title: 'Maximum Workgroup Frequency (Hard Limit)',
    icon: 'do_not_disturb',
    actionType: 'never',
    explanation:
      'This rule sets an absolute limit on how many times the person can be scheduled for a specific workgroup within a given number of days. Once the limit is reached, the person is excluded from that workgroup entirely for that window.',
    steps: [
      { icon: 'pin', text: 'Set the maximum number of times (e.g. 1)' },
      { icon: 'date_range', text: 'Set the number of days for the window (e.g. 14)' },
      { icon: 'group_work', text: 'Select the workgroup this hard limit applies to' },
      { icon: 'block', text: 'Person is excluded from that workgroup once limit is reached' },
    ],
    outcome: 'The person will be completely blocked from scheduling in the selected workgroup once they reach the limit. They may still be scheduled in other workgroups.',
    example: '"Never schedule me more than 1 time within 14 days for the Usher workgroup" — after serving as Usher once, this person cannot be an Usher again for the next 14 days.',
  },
};

/* ── Workgroup rule info data (rules 0–12) ── */
const WORKGROUP_RULES = {
  0: {
    title: 'Default Scheduling Baseline',
    icon: 'assessment',
    actionType: 'when-possible',
    explanation:
      'This is the default rule and is always active for every workgroup. It counts how many times each volunteer has been scheduled — both in past and upcoming assignments — for this specific workgroup and across the entire location. The scheduler uses these counts to distribute assignments fairly, giving priority to volunteers who have been scheduled fewer times.',
    steps: [
      { icon: 'person_search', text: 'Scheduler evaluates each volunteer being considered for an open slot' },
      { icon: 'group_work', text: 'Counts how many times the volunteer has been assigned to THIS workgroup (past + upcoming)' },
      { icon: 'location_on', text: 'Counts how many times the volunteer has been assigned across ALL workgroups at this location' },
      { icon: 'balance', text: 'Volunteers with fewer assignments are given higher priority to ensure fairness' },
    ],
    outcome: 'Volunteers are ranked by their assignment history. Those who have served less frequently will be preferred, creating an even distribution of assignments across the team.',
    example: 'If Volunteer A has been scheduled 3 times this month and Volunteer B only once, the scheduler will prefer Volunteer B for the next open slot — keeping the workload balanced.',
  },
  1: {
    title: 'Schedule Same Persons Consecutively',
    icon: 'repeat',
    actionType: 'when-possible',
    explanation:
      'This rule tries to keep the same group of volunteers scheduled across consecutive instances or dates. It is useful when continuity matters — for example, keeping the same worship team for back-to-back services or ensuring the same group handles setup across multiple days of an event.',
    steps: [
      { icon: 'group', text: 'Scheduler looks at who was assigned to the previous instance/date' },
      { icon: 'arrow_forward', text: 'Attempts to re-assign those same volunteers to the next consecutive date' },
      { icon: 'event_repeat', text: 'Continues this pattern across multiple consecutive instances' },
    ],
    outcome: 'The same volunteers will be preferred for consecutive scheduling slots, providing continuity and reducing the need for re-training or handoffs between different people.',
    example: 'If the A/V Team had John, Sarah, and Mike on Saturday morning, the scheduler will try to assign John, Sarah, and Mike again for Sunday morning — keeping the experienced team together for the weekend.',
  },
  2: {
    title: 'Filter by Skill Level (Above Threshold)',
    icon: 'trending_up',
    actionType: 'when-possible',
    explanation:
      'This rule filters volunteers based on their skill level, preferring those who are at or above a specified level. This is ideal for high-profile events, special services, or situations where you need your most experienced volunteers. Skill levels typically range from 1 (beginner) to 5 (expert).',
    steps: [
      { icon: 'star', text: 'Set the minimum skill level threshold (e.g. level 4)' },
      { icon: 'filter_alt', text: 'Scheduler evaluates each volunteer\'s skill level for the workgroup\'s skill' },
      { icon: 'trending_up', text: 'Volunteers at or above the threshold are given priority' },
      { icon: 'event_available', text: 'Lower-skilled volunteers may still be considered if not enough high-skilled are available' },
    ],
    outcome: 'Your most skilled and experienced volunteers will be prioritized for this workgroup. Less experienced volunteers will only be considered if there are not enough qualified candidates available.',
    example: 'For Easter Sunday worship, set the threshold to skill level 4. The scheduler will first try to fill slots with level 4 and 5 musicians before considering level 3 volunteers.',
  },
  3: {
    title: 'Filter by Skill Level (Below Threshold)',
    icon: 'trending_down',
    actionType: 'when-possible',
    explanation:
      'This rule is the opposite of Rule #2 — it prefers volunteers who are below a specified skill level. This is useful for lower-priority events where you want to give newer or less experienced volunteers an opportunity to serve and build their skills, while reserving expert volunteers for bigger events.',
    steps: [
      { icon: 'star_half', text: 'Set the maximum skill level threshold (e.g. level 3)' },
      { icon: 'filter_alt', text: 'Scheduler evaluates each volunteer\'s skill level' },
      { icon: 'trending_down', text: 'Volunteers at or below the threshold are given priority' },
      { icon: 'school', text: 'Gives less experienced volunteers a chance to practice and grow' },
    ],
    outcome: 'Less experienced volunteers will be prioritized, giving them opportunities to develop their skills. Your expert volunteers are kept available for more demanding events.',
    example: 'For a Wednesday night practice service, set the threshold to skill level 3. The scheduler will prefer level 1–3 sound techs, saving your level 4–5 engineers for Sunday worship.',
  },
  4: {
    title: 'Schedule Same Person Across Activities',
    icon: 'date_range',
    actionType: 'when-possible',
    explanation:
      'This rule is designed for organizations with multiple activities or services in close proximity (e.g. a Saturday evening and Sunday morning service). It tries to schedule the same volunteer for the same role across these related activities. This is especially valuable for roles that require preparation or familiarity with the specific event content, like a sound engineer who has already rehearsed the playlist.',
    steps: [
      { icon: 'event', text: 'Scheduler identifies related activities/instances within a close time window' },
      { icon: 'person', text: 'Looks at who is assigned to the first activity' },
      { icon: 'arrow_forward', text: 'Tries to assign the same person to the corresponding slot in the next activity' },
      { icon: 'consistency', text: 'Provides continuity across multiple services in the same weekend' },
    ],
    outcome: 'The same volunteer will be preferred for the same role across multiple activities, reducing preparation time and leveraging their familiarity with the event setup.',
    example: 'A sound engineer scheduled for Saturday\'s 6 PM service will also be preferred for Sunday\'s 9 AM and 11 AM services — since they already know the playlist and sound settings for that weekend.',
  },
  5: {
    title: 'Limit Scheduling Frequency (Overall)',
    icon: 'speed',
    actionType: 'when-possible',
    explanation:
      'This rule helps prevent volunteer burnout by limiting how often a person is scheduled within a given time window. Unlike the hard-limit user rule, this is a soft preference at the workgroup level — the scheduler will try to stay within the limit but won\'t completely exclude a volunteer if it needs to fill a slot.',
    steps: [
      { icon: 'pin', text: 'Set the maximum number of assignments (e.g. 2)' },
      { icon: 'date_range', text: 'Set the time window in days (e.g. 14 days)' },
      { icon: 'calculate', text: 'Scheduler counts each volunteer\'s recent assignments' },
      { icon: 'balance', text: 'Volunteers over the limit are deprioritized but not excluded' },
    ],
    outcome: 'Volunteers who have already served frequently will be deprioritized, encouraging a more even distribution. However, they can still be scheduled if no other candidates are available.',
    example: 'Set to max 2 times within 14 days. A greeter who has already been scheduled twice in the last two weeks will be ranked lower, but if no other greeters are available, they may still be assigned.',
  },
  6: {
    title: 'Limit Scheduling Frequency (Workgroup-Specific)',
    icon: 'group_work',
    actionType: 'when-possible',
    explanation:
      'Similar to Rule #5 but specific to this workgroup only. It limits how often a volunteer is scheduled for THIS particular workgroup within a time window. This is useful when a volunteer serves in multiple workgroups and you want to make sure they aren\'t overloaded in just one area.',
    steps: [
      { icon: 'pin', text: 'Set the maximum number of assignments for this workgroup (e.g. 1)' },
      { icon: 'date_range', text: 'Set the time window in days (e.g. 7 days)' },
      { icon: 'group_work', text: 'Scheduler counts assignments specifically for this workgroup' },
      { icon: 'balance', text: 'Volunteers over the workgroup-specific limit are deprioritized' },
    ],
    outcome: 'Volunteers are rotated more evenly within this workgroup. A volunteer who just served in this workgroup recently will be ranked lower for the next assignment.',
    example: 'Set to max 1 time within 7 days for the Ushers workgroup. If an usher served last Sunday, the scheduler will prefer other ushers this week — but may still assign them if the team is short.',
  },
  7: {
    title: 'Require Workgroup Preference',
    icon: 'favorite',
    actionType: 'when-possible',
    explanation:
      'This rule filters volunteers by checking if they have explicitly expressed a preference for this workgroup. Only volunteers who have added this workgroup to their preferences will be considered first. This ensures that people who actually want to serve in this area are prioritized, leading to higher satisfaction and reliability.',
    steps: [
      { icon: 'favorite_border', text: 'Scheduler checks each volunteer\'s workgroup preferences' },
      { icon: 'filter_alt', text: 'Volunteers who have this workgroup in their preferences are prioritized' },
      { icon: 'person_off', text: 'Volunteers without this preference are deprioritized (but not excluded)' },
    ],
    outcome: 'Volunteers who have specifically expressed interest in this workgroup will be scheduled first. Others may still be considered if the preferred volunteers cannot fill all slots.',
    example: 'For the Choir workgroup, only volunteers who have "Choir" in their workgroup preferences will be prioritized. A volunteer skilled in singing but who hasn\'t set Choir as a preference will be ranked lower.',
  },
  8: {
    title: 'Penalize Declined Assignments',
    icon: 'thumb_down',
    actionType: 'when-possible',
    explanation:
      'This rule deprioritizes volunteers who have a history of declining assignments for the workgroup\'s activity dates. If a volunteer frequently says "no" to assignments, the scheduler will rank them lower so that more reliable volunteers are considered first. This helps improve overall attendance and reduces the need for last-minute replacements.',
    steps: [
      { icon: 'history', text: 'Scheduler reviews each volunteer\'s assignment response history' },
      { icon: 'thumb_down', text: 'Identifies volunteers who have declined assignments in the past' },
      { icon: 'trending_down', text: 'Volunteers with more declines are ranked lower' },
      { icon: 'verified', text: 'Volunteers who consistently accept assignments are preferred' },
    ],
    outcome: 'Volunteers with a track record of declining will be deprioritized, while those who are reliable and consistently accept assignments will be scheduled more often.',
    example: 'If a volunteer has declined 4 out of the last 5 assignments for Sunday services, the scheduler will rank them lower and prefer volunteers who have a better acceptance rate.',
  },
  9: {
    title: 'Avoid Repeat Scheduling Within Days',
    icon: 'event_busy',
    actionType: 'when-possible',
    explanation:
      'This rule prevents the same volunteer from being scheduled too close together, which is especially important for organizations with multiple services within a few days (like Saturday evening + Sunday morning). It ensures volunteers get adequate rest between assignments and that more people have the opportunity to serve.',
    steps: [
      { icon: 'date_range', text: 'Set the minimum number of days between assignments' },
      { icon: 'event', text: 'Scheduler checks each volunteer\'s most recent assignment date' },
      { icon: 'timer', text: 'If the gap is shorter than the specified days, the volunteer is deprioritized' },
      { icon: 'groups', text: 'Other available volunteers are preferred instead' },
    ],
    outcome: 'Volunteers will not be scheduled for back-to-back services (unless necessary), giving them rest time and distributing opportunities more evenly across the volunteer pool.',
    example: 'Set to 2 days. If a volunteer is scheduled for Saturday at 6 PM, the scheduler will try to avoid scheduling them again for Sunday at 9 AM — but may still do so if the team is too small.',
  },
  10: {
    title: 'Gender Balance Requirement',
    icon: 'wc',
    actionType: 'when-possible',
    explanation:
      'This rule ensures that both male and female volunteers are represented in each shift. This is particularly important for workgroups like Nursery, Children\'s Ministry, or Security where having mixed-gender teams is a safety requirement or organizational policy. The scheduler will try to include at least one volunteer of each specified gender.',
    steps: [
      { icon: 'wc', text: 'Specify the required gender mix (e.g. must include Female)' },
      { icon: 'person_search', text: 'Scheduler evaluates the gender of each volunteer in the candidate pool' },
      { icon: 'group', text: 'Ensures the selected team includes the required gender representation' },
      { icon: 'verified_user', text: 'Meets safety and policy requirements for mixed-gender teams' },
    ],
    outcome: 'Each shift will include volunteers of the required gender, ensuring compliance with safety policies and organizational standards.',
    example: 'For the Nursery workgroup, require that at least one female volunteer is present. If only male volunteers are available, the scheduler will flag the slot as needing attention. This ensures children are supervised by a gender-diverse team as required by policy.',
  },
  11: {
    title: 'Prefer Highest Skilled Volunteers',
    icon: 'military_tech',
    actionType: 'when-possible',
    explanation:
      'This rule automatically prioritizes volunteers with the highest skill levels without requiring you to specify a specific threshold. The scheduler ranks all eligible volunteers by their skill level and prefers those at the top. This is a simpler alternative to Rules #2/#3 when you just want "the best available" without picking a cutoff number.',
    steps: [
      { icon: 'star', text: 'Scheduler retrieves each volunteer\'s skill level for the workgroup\'s skill' },
      { icon: 'sort', text: 'Sorts volunteers by skill level from highest to lowest' },
      { icon: 'emoji_events', text: 'Higher-skilled volunteers get priority for open slots' },
      { icon: 'groups', text: 'Lower-skilled volunteers are still considered as backup options' },
    ],
    outcome: 'Your most talented and experienced volunteers will naturally rise to the top of the scheduling queue. No specific threshold is needed — the scheduler simply prefers higher skills.',
    example: 'For the Worship Band, if you have guitarists at levels 2, 3, 4, and 5 — the level 5 guitarist gets first priority, then level 4, and so on down the list.',
  },
  12: {
    title: 'Penalize Frequent Decliners',
    icon: 'do_not_disturb_alt',
    actionType: 'when-possible',
    explanation:
      'This rule is similar to Rule #8 but focuses specifically on the rate of declined assignments rather than just the count. Volunteers who have a high decline rate (relative to their total assignments) will be deprioritized. This ensures that volunteers who are consistently unreliable are considered less often, reducing wasted scheduling effort and last-minute scrambles for replacements.',
    steps: [
      { icon: 'analytics', text: 'Scheduler calculates each volunteer\'s decline rate (declines ÷ total assignments)' },
      { icon: 'trending_down', text: 'Volunteers with higher decline rates are ranked lower' },
      { icon: 'verified', text: 'Reliable volunteers (low decline rate) are ranked higher' },
      { icon: 'schedule', text: 'Reduces the need for last-minute replacements' },
    ],
    outcome: 'Volunteers who tend to decline assignments will be gradually deprioritized. This encourages reliability and ensures your schedule is filled with people who actually show up.',
    example: 'If Volunteer A has declined 80% of assignments and Volunteer B has declined only 10%, the scheduler will strongly prefer Volunteer B — leading to a more reliable schedule with fewer no-shows.',
  },
};

/* ── Workgroup rule info (generic fallback for unknown rules) ── */
const WORKGROUP_RULE_FALLBACK = {
  title: 'Workgroup Rule',
  icon: 'rule',
  actionType: 'when-possible',
  explanation: 'This rule controls how the scheduler handles assignments for this workgroup. Configure the inputs and save to apply.',
  steps: [
    { icon: 'settings', text: 'Configure the rule parameters' },
    { icon: 'save', text: 'Save the rule to apply it' },
    { icon: 'event_available', text: 'Scheduler evaluates the rule during schedule generation' },
  ],
  outcome: 'The scheduler will apply this rule when generating schedules for this workgroup.',
  example: 'Refer to the rule description above for specific behavior.',
};

const info = computed(() => {
  if (props.customInfo) return props.customInfo;
  if (props.ruleContext === 'user' && USER_RULES[props.ruleId]) {
    return USER_RULES[props.ruleId];
  }
  if (props.ruleContext === 'workgroup' && WORKGROUP_RULES[props.ruleId]) {
    return WORKGROUP_RULES[props.ruleId];
  }
  return WORKGROUP_RULE_FALLBACK;
});

const effectiveActionType = computed(() => {
  const at = info.value.actionType;
  if (at === 'both') return 'both';
  return at === 'never' ? 'never' : 'when-possible';
});

const actionBgClass = computed(() => {
  const at = effectiveActionType.value;
  if (at === 'never') return 'rim-bg-never';
  if (at === 'both') return 'rim-bg-both';
  return 'rim-bg-whenpossible';
});

const actionBadgeClass = computed(() => {
  const at = effectiveActionType.value;
  if (at === 'never') return 'rim-badge-never';
  if (at === 'both') return 'rim-badge-both';
  return 'rim-badge-whenpossible';
});

const actionBoxClass = computed(() => {
  const at = effectiveActionType.value;
  if (at === 'never') return 'rim-box-never';
  if (at === 'both') return 'rim-box-both';
  return 'rim-box-whenpossible';
});

const actionIcon = computed(() => {
  const at = effectiveActionType.value;
  if (at === 'never') return 'block';
  if (at === 'both') return 'swap_vert';
  return 'tune';
});

const actionLabel = computed(() => {
  const at = effectiveActionType.value;
  if (at === 'never') return 'NEVER';
  if (at === 'both') return 'NEVER / WHEN POSSIBLE';
  return 'WHEN POSSIBLE';
});

const actionExplanation = computed(() => {
  const at = effectiveActionType.value;
  if (at === 'never')
    return 'This is a hard rule. If the condition is met, the person will be completely excluded from scheduling. There is no flexibility.';
  if (at === 'both')
    return 'This rule supports both modes. "Never" will strictly block the person. "When Possible" will adjust the probability but still allow scheduling if needed.';
  return 'This is a soft preference. The scheduler will try to honor this rule, but the person will NOT be excluded if it cannot be satisfied. It adjusts probability, not eligibility.';
});
</script>

<style scoped>
.rule-info-overlay {
  position: fixed;
  inset: 0;
  z-index: 2060;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.rule-info-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: rimSlideUp 0.25s ease;
}

@keyframes rimSlideUp {
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Header ── */
.rim-header {
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  color: #fff;
}
.rim-header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.rim-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rim-icon-wrap i { font-size: 24px; }
.rim-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
}
.rim-close {
  background: rgba(255,255,255,0.2);
  border: none;
  border-radius: 8px;
  color: #fff;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}
.rim-close:hover { background: rgba(255,255,255,0.35); }

/* Header backgrounds */
.rim-bg-never   { background: linear-gradient(135deg, #e53935, #c62828); }
.rim-bg-whenpossible { background: linear-gradient(135deg, #ff9800, #e65100); }
.rim-bg-both    { background: linear-gradient(135deg, #667eea, #764ba2); }

/* Action badge */
.rim-action-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 4px;
}
.rim-badge-never       { background: rgba(255,255,255,0.25); color: #fff; }
.rim-badge-whenpossible { background: rgba(255,255,255,0.25); color: #fff; }
.rim-badge-both        { background: rgba(255,255,255,0.25); color: #fff; }

/* ── Body ── */
.rim-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}

/* Action type box */
.rim-action-box {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  margin-bottom: 1.25rem;
  font-size: 0.82rem;
  line-height: 1.5;
}
.rim-action-icon-lg {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rim-action-icon-lg i { font-size: 20px; }
.rim-box-never {
  background: #fce4ec;
  border-left: 4px solid #e53935;
}
.rim-box-never .rim-action-icon-lg {
  background: #e53935;
  color: #fff;
}
.rim-box-whenpossible {
  background: #fff3e0;
  border-left: 4px solid #ff9800;
}
.rim-box-whenpossible .rim-action-icon-lg {
  background: #ff9800;
  color: #fff;
}
.rim-box-both {
  background: #ede7f6;
  border-left: 4px solid #667eea;
}
.rim-box-both .rim-action-icon-lg {
  background: #667eea;
  color: #fff;
}

/* Sections */
.rim-section {
  margin-bottom: 1.25rem;
}
.rim-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.88rem;
  font-weight: 700;
  color: #344767;
  margin-bottom: 0.5rem;
}
.rim-section-title i {
  font-size: 18px;
  color: #667eea;
}
.rim-text {
  font-size: 0.82rem;
  color: #555;
  line-height: 1.6;
  margin: 0;
}

/* Steps */
.rim-steps {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.rim-step {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  position: relative;
  padding-bottom: 0.25rem;
}
.rim-step-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  margin-top: 2px;
}
.rim-step-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f0f0f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rim-step-icon-wrap i { font-size: 18px; color: #555; }
.rim-step-text {
  font-size: 0.8rem;
  color: #444;
  line-height: 1.5;
  padding-top: 5px;
}
.rim-step-arrow {
  position: absolute;
  left: 10px;
  bottom: -6px;
  color: #bbb;
}
.rim-step-arrow i { font-size: 16px; }

/* Outcome */
.rim-outcome {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.5;
}
.rim-outcome-icon { font-size: 22px; }

/* Example */
.rim-example {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #f5f7fa;
  border-left: 4px solid #667eea;
  font-size: 0.82rem;
  color: #555;
  line-height: 1.6;
}
.rim-example-icon {
  color: #667eea;
  font-size: 22px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* ── Footer ── */
.rim-footer {
  padding: 0.75rem 1.5rem;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}
.rim-footer-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 24px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: box-shadow 0.15s;
}
.rim-footer-btn:hover {
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.5);
}
</style>
